/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { useEffect, useRef, useState } from 'react';
// Local
import HeaderDesktop from 'components/authorable/shared/site-structure/Header/HeaderDesktop';
import HeaderMobile from 'components/authorable/shared/site-structure/Header/HeaderMobile';
import { tv } from 'tailwind-variants';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import { HeaderProvider, useHeader } from './HeaderContext';
import { getTestProps } from 'lib/testing/utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const HeaderContent = (props)=>{
    const isEditing = useIsEditing();
    const { isOverlayVisible, handleOverlayChange, setIsMobile } = useHeader();
    const isTablet = useIsTablet();
    const [mobileHeaderTotalHeight, setMobileHeaderTotalHeight] = useState(0);
    const headerMobileRef = useRef(null);
    const setOverlayPosition = ()=>{
        const mobileHeaderHeight = headerMobileRef.current?.getBoundingClientRect().height;
        const alertContainer = document.getElementById('alert-container');
        if (alertContainer) {
            const alertContainerHeight = alertContainer.getBoundingClientRect().height;
            if (mobileHeaderHeight && alertContainerHeight) {
                const rect = headerMobileRef.current?.getBoundingClientRect();
                const mobileHeaderHeight = (rect?.height || 0) + Math.abs(rect?.top || 0);
                setMobileHeaderTotalHeight(mobileHeaderHeight || 0);
            }
        } else {
            if (mobileHeaderHeight) {
                setMobileHeaderTotalHeight(mobileHeaderHeight || 0);
            }
        }
    };
    useEffect(()=>{
        setOverlayPosition();
        window.addEventListener('scroll', setOverlayPosition, {
            passive: true
        });
        return ()=>{
            window.removeEventListener('scroll', setOverlayPosition);
        };
    }, [
        headerMobileRef,
        isOverlayVisible
    ]);
    // Update isMobile state (treat tablet and smaller as mobile header)
    useEffect(()=>{
        setIsMobile(isTablet);
    }, [
        isTablet,
        setIsMobile
    ]);
    // Update overlay visibility whenever any menu state changes
    useEffect(()=>{
        handleOverlayChange();
    }, [
        handleOverlayChange
    ]);
    // Lock body scroll when overlay is visible (menu/dropdown is open)
    useEffect(()=>{
        // Store original overflow value before any changes
        const originalOverflow = window.getComputedStyle(document.body).overflow;
        if (isOverlayVisible) {
            // Prevent background scroll
            document.body.style.overflow = 'hidden';
        } else {
            // Restore scroll when menu closes
            document.body.style.overflow = originalOverflow;
        }
        // Cleanup: always restore on unmount
        return ()=>{
            document.body.style.overflow = originalOverflow;
        };
    }, [
        isOverlayVisible
    ]);
    const { base, overlay } = TAILWIND_VARIANTS({
        isEditing,
        isOverlayVisible
    });
    /*
   * Rendering
   */ if (!props.fields) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("header", {
                id: "header",
                "data-component": "authorable/shared/site-structure/header/header",
                ...editingHydrationProps(isEditing),
                ...getTestProps(`component-header-` + props?.rendering?.uid),
                className: base(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "hidden lg:block",
                        children: /*#__PURE__*/ _jsx(HeaderDesktop, {
                            ...props
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "block lg:hidden",
                        ref: headerMobileRef,
                        children: /*#__PURE__*/ _jsx(HeaderMobile, {
                            ...props,
                            mobileHeaderTotalHeight: mobileHeaderTotalHeight
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: overlay(),
                style: {
                    top: `${mobileHeaderTotalHeight}px`
                }
            })
        ]
    });
};
export const Default = (props)=>{
    return /*#__PURE__*/ _jsx(HeaderProvider, {
        children: /*#__PURE__*/ _jsx(HeaderContent, {
            ...props
        })
    });
};
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'top-0',
            'bg-component-header-nav-bg',
            'w-full',
            'z-50',
            'transition-transform',
            'duration-300'
        ],
        overlay: [
            'fixed',
            'left-0',
            'bottom-0',
            'bg-black',
            'opacity-[0.7]',
            'z-40',
            'right-0'
        ]
    },
    variants: {
        isEditing: {
            false: {
                base: [
                    'sticky'
                ]
            },
            true: {
                base: [
                    'relative'
                ]
            }
        },
        isOverlayVisible: {
            true: {
                overlay: [
                    'block'
                ]
            },
            false: {
                overlay: [
                    'hidden'
                ]
            }
        }
    }
});
